/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import quarterOfYear from 'dayjs/plugin/quarterOfYear'; 
import { currencyFormatter } from "../../../helpers/currencyHelper";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import { ExportPdf, ExportCsv } from "@material-table/exporters";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import FileOpenOutlinedIcon from "@mui/icons-material/FileOpenOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import { esES } from "@mui/material/locale";
import Modal from "../../Modal/Modal";
import { BACKEND_URL } from "../../../config/config";
import axios from "axios";
import { OpenDocumentList } from "../../Dashboard";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import { Button, Select, DatePicker, Space, message } from "antd";
import { OpenModalAccount } from "../../Account";
import { OpenModalProposal } from "../../Proposal/OpenModalProposal/OpenModalProposal";
import { CreditCardOutlined } from "@mui/icons-material";
import {
  Alert as MUIAlert,
  AlertTitle,
  Box,
  Divider,
  Grid,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

const { RangePicker } = DatePicker;

export function InvoicesList({ invoices, title, pageSize, chart }) {
  const [isVisibleModal, setIsVisibleModal] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalContent, setModalContent] = useState(null);

  // Filtros
  const [dateFilter, setDateFilter] = useState(undefined); // etiqueta quick filter
  const [statusFilter, setStatusFilter] = useState(undefined); // label mostrado
  const [dateRange, setDateRange] = useState(null); // [dayjs|null, dayjs|null]

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  dayjs.extend(quarterOfYear);
  useEffect(() => {
    const onResize = () =>
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const theme = createTheme(
    { palette: { primary: { main: "#1976d2" } } },
    esES
  );

  // ===== Helpers UI/Export =====
  const getCompanyName = (row) =>
    (row?.account?.accountName || row?.lead?.companyName || "").toString();

  const fmtDate = (d) => (d ? dayjs(d).format("MM/DD/YYYY") : "-");
  const fmtMoney = (n) => currencyFormatter(Number(n ?? 0));

  const getProposalNumber = (row) =>
    row?.opportunity?._id ?? row?.opportunity ?? "";

  const getInvoiceStatusLabel = (row) => {
    if (row?.invoiceStatus && typeof row.invoiceStatus === "object") {
      return row.invoiceStatus?.name || row.invoiceStatus?._id || "-";
    }
    if (typeof row?.invoiceStatus === "string") return row.invoiceStatus;
    return "-";
  };

  const isOpenInvoice = (row) =>
    row?.invoiceStatus?._id
      ? row.invoiceStatus._id === "JCIS"
      : row?.invoiceStatus === "JCIS";

  const renderStatus = (row) => {
    const isOpen = isOpenInvoice(row);

    return (
      <span
        className={`table-status-pill ${
          isOpen ? "table-status-pill--warning" : "table-status-pill--success"
        }`}
      >
        {isOpen ? (
          <AccessTimeOutlinedIcon className="table-status-pill__icon" />
        ) : (
          <CheckCircleOutlineIcon className="table-status-pill__icon" />
        )}
        {getInvoiceStatusLabel(row)}
      </span>
    );
  };

  // Opciones de status a partir de lo que se muestra
  const statusOptions = React.useMemo(() => {
    const set = new Set();
    (invoices || []).forEach((inv) => {
      const label = getInvoiceStatusLabel(inv);
      if (label && label !== "-") set.add(String(label));
    });
    return Array.from(set).map((label) => ({ value: label, label }));
  }, [invoices]);

  // Quick ranges (guardamos siempre dayjs en dateRange)
  const dateOptions = React.useMemo(() => {
    const map = new Map();
    map.set("THISMONTH", "This Month");
    map.set("LASTMONTH", "Last Month");
    map.set("THISQUARTER", "This Quarter");
    map.set("LASTQUARTER", "Last Quarter");
    map.set("THISYEAR", "This Year");
    map.set("LASTYEAR", "Last Year");
    return Array.from(map.entries()).map(([value, label]) => ({
      value,
      label,
    }));
  }, []);

  const handleDateFilter = (val) => {
    let from = null;
    let to = null;
    switch (val) {
      case "THISMONTH":
        from = dayjs().startOf("month");
        to = dayjs().endOf("month");
        break;
      case "LASTMONTH":
        from = dayjs().subtract(1, "month").startOf("month");
        to = dayjs().subtract(1, "month").endOf("month");
        break;
      case "THISQUARTER":
        from = dayjs().startOf("quarter");
        to = dayjs().endOf("quarter");
        break;
      case "LASTQUARTER":
        from = dayjs().subtract(1, "quarter").startOf("quarter");
        to = dayjs().subtract(1, "quarter").endOf("quarter");
        break;
      case "THISYEAR":
        from = dayjs().startOf("year");
        to = dayjs().endOf("year");
        break;
      case "LASTYEAR":
        from = dayjs().subtract(1, "year").startOf("year");
        to = dayjs().subtract(1, "year").endOf("year");
        break;
      default:
        setDateRange(null);
        setDateFilter(undefined);
        return;
    }
    setDateRange([from, to]);
    setDateFilter(val);
  };

  // RangePicker (AntD v5 usa dayjs) => guardamos dayjs
  const handleDateRangeChange = (vals) => {
    if (!vals || vals.length === 0) {
      setDateRange(null);
      setDateFilter(undefined);
      return;
    }
    const [from, to] = vals;
    setDateRange([from ? dayjs(from) : null, to ? dayjs(to) : null]);
    setDateFilter(undefined); // si el usuario define manualmente, deseleccionamos quick filter
  };

  // ===== Filtrado local (respeta Status + Date of Service) =====
  const filteredData = React.useMemo(() => {
    const [from, to] = dateRange || [null, null];

    return (invoices || []).filter((row) => {
      if (statusFilter && getInvoiceStatusLabel(row) !== statusFilter)
        return false;

      if (from || to) {
        const svc = row?.dateService ? dayjs(row.dateService) : null;
        if (!svc || !svc.isValid()) return false;
        if (from && svc.isBefore(from.startOf("day"))) return false;
        if (to && svc.isAfter(to.endOf("day"))) return false;
      }
      return true;
    });
  }, [invoices, statusFilter, dateRange]);

  const clearFilters = () => {
    setStatusFilter(undefined);
    setDateRange(null);
    setDateFilter(undefined);
  };

  // ===== Columnas (todas centradas) =====
  const centerHeader = { whiteSpace: "normal", textAlign: "center" };
  const centerCell = { whiteSpace: "normal", textAlign: "center" };

  const openInvoicescolumns = [
    {
      title: "Site",
      field: "account.accountName",
      headerStyle: centerHeader,
      cellStyle: centerCell,
      render: (rowData) => (
        <span
          onClick={() => onViewDetail(rowData)}
          className="rowClick table-entity"
        >
          <span className="table-entity__icon">
            <BusinessOutlinedIcon />
          </span>
          <span className="table-entity__text">{getCompanyName(rowData)}</span>
        </span>
      ),
    },
    {
      title: "Invoice Number",
      field: "invoiceNo",
      headerStyle: centerHeader,
      cellStyle: centerCell,
    },
    {
      title: "Date of Service",
      field: "dateService",
      headerStyle: centerHeader,
      cellStyle: centerCell,
      render: (r) => <span>{fmtDate(r?.dateService)}</span>,
    },
    {
      title: "Dispatch Number",
      field: "dispatch",
      headerStyle: centerHeader,
      cellStyle: centerCell,
    },
    {
      title: "Proposal Number",
      field: "opportunity",
      headerStyle: centerHeader,
      cellStyle: centerCell,
      render: (r) => (
        <span
          onClick={() => onViewProposalDetail(r)}
          className="rowClick table-entity"
        >
          <span className="table-entity__icon">
            <RequestQuoteOutlinedIcon />
          </span>
          <span className="table-entity__text">{getProposalNumber(r)}</span>
        </span>
      ),
    },
    {
      title: "Invoice Date",
      field: "invoiceDate",
      headerStyle: centerHeader,
      cellStyle: centerCell,
      render: (r) => <span>{fmtDate(r?.invoiceDate)}</span>,
    },
    {
      title: "Amount",
      field: "totalAmountDue",
      headerStyle: { ...centerHeader, fontVariantNumeric: "tabular-nums" },
      cellStyle: { ...centerCell, fontVariantNumeric: "tabular-nums" },
      customSort: (a, b) =>
        (Number(a?.totalAmountDue) || 0) - (Number(b?.totalAmountDue) || 0),
      render: (r) => <span>{fmtMoney(r?.totalAmountDue)}</span>,
    },
    {
      title: "Status",
      field: "invoiceStatus.name",
      headerStyle: centerHeader,
      cellStyle: centerCell,
      render: renderStatus,
    },
  ];

  // ===== Export (TODAS las filas filtradas + valores formateados) =====
  const exportColumns = [
    { title: "Site", field: "site" },
    { title: "Invoice Number", field: "invoiceNo" },
    { title: "Date of Service", field: "dateService" },
    { title: "Dispatch Number", field: "dispatch" },
    { title: "Proposal Number", field: "proposalNumber" },
    { title: "Invoice Date", field: "invoiceDate" },
    { title: "Amount", field: "amount" },
    { title: "Status", field: "status" },
  ];

  const buildExportRows = (rows) =>
    (rows || []).map((r) => ({
      site: getCompanyName(r),
      invoiceNo: r?.invoiceNo ?? "",
      dateService: fmtDate(r?.dateService),
      dispatch: r?.dispatch ?? "",
      proposalNumber: getProposalNumber(r),
      invoiceDate: fmtDate(r?.invoiceDate),
      amount: fmtMoney(r?.totalAmountDue),
      status: getInvoiceStatusLabel(r),
    }));

  // ===== Docs / View / Pay =====
  const onViewDetail = (data) => {
    setIsVisibleModal(true);
    setModalTitle("Account information");
    setModalContent(<OpenModalAccount data={data?.account || data?.lead} />);
  };

  const onViewProposalDetail = (data) => {
    setIsVisibleModal(true);
    setModalTitle(`Proposal detail # ${getProposalNumber(data)}`);
    setModalContent(
      <OpenModalProposal proposal={data.opportunity} preview={true} />
    );
  };

  const onOpenDocuments = async (rowData) => {
    try {
      const url = `${BACKEND_URL}/documentmasters/getByOpportunityId/${rowData?.opportunity?._id}`;
      const { data } = await axios.get(url);
      const dataElement = Array.isArray(data)
        ? data.map((document) => ({
            documentName: document?.documentName,
            documentType: document?.documentType?.name,
            uploadedDate: fmtDate(document?.created),
            documentPath: document?.documentPath,
          }))
        : [];
      setIsVisibleModal(true);
      setModalTitle("Invoice Documents");
      setModalContent(
        <div style={{ width: "100%" }}>
          {dataElement.length ? (
            <OpenDocumentList data={dataElement} title="" />
          ) : (
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
              }}
            >
              <span style={{ fontWeight: "bold" }}>No documents</span>
            </div>
          )}
        </div>
      );
    } catch (e) {
      console.log(e);
      message.error("Could not load documents.");
    }
  };

  const [paying, setPaying] = useState(false);

  const pay = async (rowData) => {
    const { data } = await axios
      .get(`${BACKEND_URL}/processPayment?ref=${rowData._id}`)
      .catch((err) => console.log(err));
    const paymentURL = data?.url;
    if (paymentURL) {
      window.open(
        paymentURL,
        "_blank",
        "toolbar=yes,scrollbars=yes,resizable=yes,top=500,left=500,width=600,height=800"
      );
    } else {
      message.warning("No payment link available.");
    }
  };

  const onAskPay = (rowData) => {
    const canPay =
      !!rowData?.invoiceQBOPaymentLink &&
      (rowData?.invoiceStatus?._id
        ? rowData.invoiceStatus._id === "JCIS"
        : rowData.invoiceStatus === "JCIS");

    if (!canPay) {
      message.warning("This invoice is not eligible for online payment.");
      return;
    }

    const amount = Number(rowData?.totalAmountDue || 0);
    const discountRate = 0.03;
    const fee = amount * discountRate;
    const totalWithDiscount = amount + fee;

    setModalTitle("Confirm payment");
    setModalContent(
      <Box sx={{ width: "100%", maxWidth: 560, px: 2, py: 1, mx: "auto" }}>
        <MUIAlert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ mb: 1.5 }}
        >
          <AlertTitle sx={{ fontWeight: 600, mb: 0.5 }}>Heads up</AlertTitle>
          A 3% fee will be applied at checkout.
          <br />
          You will be redirected to our secure payment page to complete the
          transaction.
        </MUIAlert>

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ mb: 1 }}>
          <Grid container rowSpacing={0.5} columnSpacing={2}>
            <Grid item xs={6} sx={{ color: "text.secondary" }}>
              Invoice
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right", fontWeight: 600 }}>
              #{rowData?.invoiceNo}
            </Grid>

            <Grid item xs={6} sx={{ color: "text.secondary" }}>
              Date
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              {fmtDate(rowData?.invoiceDate)}
            </Grid>

            <Grid item xs={6} sx={{ color: "text.secondary" }}>
              Site
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              {getCompanyName(rowData)}
            </Grid>
          </Grid>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Box>
          <Grid container rowSpacing={0.5} columnSpacing={2}>
            <Grid item xs={6} sx={{ color: "text.secondary" }}>
              Amount
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              {fmtMoney(amount)}
            </Grid>

            <Grid item xs={6} sx={{ color: "text.secondary" }}>
              Fee (3%)
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right" }}>
              +{fmtMoney(fee)}
            </Grid>

            <Grid item xs={6} sx={{ fontWeight: 600 }}>
              Total to pay
            </Grid>
            <Grid item xs={6} sx={{ textAlign: "right", fontWeight: 600 }}>
              {fmtMoney(totalWithDiscount)}
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}
        >
          <Button onClick={() => setIsVisibleModal(false)}>Cancel</Button>
          <Button
            type="primary"
            icon={<CreditCardOutlined />}
            loading={paying}
            onClick={() => handleProceedPay(rowData)}
          >
            Confirm & pay
          </Button>
        </Box>
      </Box>
    );
    setIsVisibleModal(true);
  };

  const handleProceedPay = async (rowData) => {
    try {
      setPaying(true);
      await pay(rowData);
      setIsVisibleModal(false);
    } finally {
      setPaying(false);
    }
  };

  return (
    <div>
      <div className="listContainer">
        <ThemeProvider theme={theme}>
          <MaterialTable
            columns={openInvoicescolumns}
            className="genericTable"
            components={{
              Toolbar: (props) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    fontWeight: "bolder",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <MTableToolbar {...props} />
                  </div>
                  <Space wrap align="center">
                    <Select
                      allowClear
                      placeholder="Quick Filter"
                      className="filter-select"
                      value={dateFilter}
                      onChange={handleDateFilter}
                      options={dateOptions}
                    />
                    <Select
                      allowClear
                      placeholder="Filter by Status"
                      className="filter-select"
                      value={statusFilter}
                      onChange={(v) => setStatusFilter(v)}
                      options={statusOptions}
                    />
                    <RangePicker
                      value={dateRange || undefined} // dayjs[] | undefined
                      onChange={handleDateRangeChange}
                      className="filter-range-picker"
                      format="MM/DD/YYYY"
                      placeholder={[
                        "From (Date of Service)",
                        "To (Date of Service)",
                      ]}
                      allowClear
                    />
                    <Button className="filter-clear-btn" onClick={clearFilters}>
                      Clear
                    </Button>
                  </Space>
                </div>
              ),
            }}
            data={filteredData}
            actions={
              chart
                ? []
                : [
                    {
                      icon: () => (
                        <FileOpenOutlinedIcon className="table-action-icon table-action-icon--documents" />
                      ),
                      tooltip: "Open Documents",
                      onClick: (_e, rd) => onOpenDocuments(rd),
                    },
                    {
                      icon: () => (
                        <PaidOutlinedIcon className="table-action-icon table-action-icon--payment" />
                      ),
                      tooltip: "Pay invoice",
                      onClick: (_e, rd) => onAskPay(rd),
                    },
                  ]
            }
            options={{
              headerStyle: {
                position: "sticky",
                top: 0,
                zIndex: 100,
                backgroundColor: "white",
                textAlign: "center",
              },
              maxBodyHeight: `${windowSize.height - 250}px`,
              minBodyHeight: `${windowSize.height - 250}px`,
              actionsColumnIndex: -1,
              pageSize: pageSize || 20,
              filtering: false,
              exportMenu: [
                {
                  label: "Export to PDF",
                  exportFunc: () => {
                    const rows = buildExportRows(filteredData);
                    ExportPdf(
                      exportColumns,
                      rows,
                      "Invoice Report - " + new Date().toISOString()
                    );
                  },
                },
                {
                  label: "Export to CSV",
                  exportFunc: () => {
                    const rows = buildExportRows(filteredData);
                    ExportCsv(
                      exportColumns,
                      rows,
                      "Invoice Report - " + new Date().toISOString()
                    );
                  },
                },
              ],
            }}
            style={{ width: "100%" }}
            title={title}
          />
        </ThemeProvider>
      </div>

      <Modal
        title={modalTitle}
        open={isVisibleModal}
        setIsVisible={setIsVisibleModal}
        className={
          modalTitle === "Approve this proposal" ||
          modalTitle === "Confirm payment"
            ? null
            : "openModalDocumentsList"
        }
      >
        {modalContent}
      </Modal>
    </div>
  );
}
